import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eligibleExperiencesQueryOptions, GUIDED_EXPERIENCE_QUERY_KEYS } from '@/api/queries/guided-experiences';
import * as guidedExperiencesApi from '@/api/endpoints/guided-experiences';
import type { EligibleTourWire, TourStepActionWire, TourStepWire } from '@/api/schemas/feature-tour';
import { tourDefinitionWireSchema } from '@/api/schemas/feature-tour';
import type {
  EligibleTour,
  FeatureToursStatus,
  TourDefinition,
  TourStep,
  TourStepAction,
  UseFeatureToursReturn,
} from '../types';

function mapStepAction(wire: TourStepActionWire): TourStepAction {
  return {
    label: wire.label,
    behavior: wire.behavior as TourStepAction['behavior'],
    ...(wire.navigate_to !== undefined ? { navigateTo: wire.navigate_to } : {}),
  };
}

function mapStep(wire: TourStepWire): TourStep {
  return {
    stepIndex: wire.step_index,
    title: wire.title,
    body: wire.body,
    targetSelector: wire.target_selector,
    spotlight: wire.spotlight,
    primaryAction: mapStepAction(wire.primary_action),
    ...(wire.secondary_action !== undefined
      ? { secondaryAction: mapStepAction(wire.secondary_action) }
      : {}),
  };
}

function parseDefinition(definitionJson: string): TourDefinition {
  try {
    const raw = JSON.parse(definitionJson);
    const parsed = tourDefinitionWireSchema.parse(raw);
    return { steps: parsed.steps.map(mapStep) };
  } catch {
    return { steps: [] };
  }
}

function mapTour(wire: EligibleTourWire): EligibleTour {
  return {
    slug: wire.slug,
    version: wire.version,
    title: wire.title,
    description: wire.description,
    triggerType: wire.trigger_type,
    contextRoute: wire.context_route,
    priority: wire.priority,
    definition: parseDefinition(wire.definition),
  };
}

export type { UseFeatureToursReturn } from '../types';

export function useFeatureTours(): UseFeatureToursReturn {
  const queryClient = useQueryClient();

  const { data: eligibilityData, isLoading, isError } = useQuery(
    eligibleExperiencesQueryOptions()
  );

  const status: FeatureToursStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (isError) return 'error';
    return 'ready';
  }, [isLoading, isError]);

  const tours = useMemo<EligibleTour[]>(
    () => (eligibilityData?.tours ?? []).map(mapTour),
    [eligibilityData]
  );

  const digestTours = useMemo<EligibleTour[]>(
    () => tours.filter((t) => t.triggerType === 'LOGIN_DIGEST' || t.triggerType === 'BOTH'),
    [tours]
  );

  const contextualTours = useMemo<EligibleTour[]>(
    () => tours.filter((t) => t.triggerType === 'CONTEXTUAL' || t.triggerType === 'BOTH'),
    [tours]
  );

  const invalidateEligible = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: GUIDED_EXPERIENCE_QUERY_KEYS.eligible(),
    });
  }, [queryClient]);

  const startTour = useCallback(
    async (slug: string, version: number) => {
      await guidedExperiencesApi.startExperience(slug, version);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  const dismissTour = useCallback(
    async (slug: string) => {
      await guidedExperiencesApi.dismissExperience(slug);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  const completeTour = useCallback(
    async (slug: string) => {
      await guidedExperiencesApi.completeExperience(slug);
      invalidateEligible();
    },
    [invalidateEligible]
  );

  return {
    status,
    tours,
    digestTours,
    contextualTours,
    startTour,
    dismissTour,
    completeTour,
  };
}
